import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import AdminLayout from "../../../layouts/AdminLayout";
import Button from "../../../components/ui/Button";
import SelectDropdown from "../../../components/ui/SelectDropdown";
import FileUpload from "../../../components/ui/FileUpload";
import FormField from "../../../components/ui/FormField";
import StatusBadge from "../../../components/ui/StatusBadge";
import { ArrowLeft, Download, CheckCircle, AlertTriangle } from "lucide-react";
import { getCurrentRoleKey, getStoredAuthUser } from "../../../services/authService";
import { getSocieties } from "../../../services/societyService";
import { downloadResidentImportTemplate, importResidentsFile } from "../../../services/residentImportService";
import { residentImportSchema, zodErrorToFirstMessage } from "../../../schemas/formSchemas";
import styles from "./ResidentImport.module.css";

const ResidentImport = () => {
  const navigate = useNavigate();
  const currentUser = getStoredAuthUser();
  const roleKey = getCurrentRoleKey();
  const canPickSociety = useMemo(() => roleKey === "super-admin" || roleKey === "admin", [roleKey]);

  const [busy, setBusy] = useState(false);
  const [dryRun, setDryRun] = useState(true);
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);

  const [societies, setSocieties] = useState([]);
  const [societyId, setSocietyId] = useState(currentUser?.society_id ? String(currentUser.society_id) : "");

  const loadSocieties = useCallback(async () => {
    try {
      const data = await getSocieties();
      setSocieties(data || []);
      if (canPickSociety && !societyId && data?.length) setSocietyId(String(data[0].society_id));
    } catch (err) {
      toast.error(err?.message || "Unable to load societies.");
    }
  }, [canPickSociety, societyId]);

  useEffect(() => {
    if (canPickSociety) void loadSocieties();
  }, [canPickSociety, loadSocieties]);

  const handleDownloadTemplate = async () => {
    try {
      const csvText = await downloadResidentImportTemplate();
      const blob = new Blob([csvText], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "residents_import_template.csv";
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err?.message || "Template download failed.");
    }
  };

  const handleImport = async () => {
    const parsed = residentImportSchema.safeParse({ file, dryRun, societyId: canPickSociety ? societyId : undefined });
    if (!parsed.success) {
      toast.error(zodErrorToFirstMessage(parsed.error));
      return;
    }

    setBusy(true);
    setResult(null);
    try {
      const payload = await importResidentsFile(parsed.data.file, {
        dryRun: parsed.data.dryRun,
        societyId: parsed.data.societyId,
      });
      if (payload?.success) {
        setResult(payload);
        toast.success(dryRun ? "Dry run completed." : "Import completed.");
      } else {
        toast.error(payload?.message || "Import failed.");
      }
    } catch (err) {
      toast.error(err?.message || "Import failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AdminLayout>
      <div className={styles.card} style={{ maxWidth: "900px", margin: "0 auto", padding: "24px" }}>
        <div className={styles.header} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
          <div>
            <h2 className={styles.title} style={{ margin: "0 0 4px 0", fontSize: "20px", fontWeight: 700 }}>Import Residents & Occupants</h2>
            <p className={styles.subtitle} style={{ margin: 0, fontSize: "13px", color: "var(--color-text-muted)" }}>
              Upload an Excel/CSV spreadsheet to batch-provision property residents, family members, and vehicle passes.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={() => navigate("/users")}>
            <ArrowLeft size={15} style={{ marginRight: "4px" }} /> Back
          </Button>
        </div>

        <div className={styles.help} >
          <div className={styles.helpLine} style={{ fontSize: "13px", marginBottom: "6px" }}>
            <strong>Required columns:</strong> <code className={styles.code}>full_name</code>, <code className={styles.code}>email_id</code>,{" "}
            <code className={styles.code}>mobile_number</code>, <code className={styles.code}>user_type</code>{" "}
            <span className={styles.muted}>(Owner / Tenant / User)</span>
          </div>
          <div className={styles.helpLine} >
            <strong>Optional columns:</strong> <code className={styles.code}>gender</code>, <code className={styles.code}>flat_ids_owner</code>, <code className={styles.code}>flat_ids_tenant</code>,{" "}
            <code className={styles.code}>parking_slot_codes</code>, <code className={styles.code}>family_members</code>, <code className={styles.code}>vehicles</code>
          </div>
        </div>

        <div style={{ display: "flex", gap: "10px", alignItems: "center", marginBottom: "20px", flexWrap: "wrap" }}>
          <Button variant="outline" size="sm" onClick={handleDownloadTemplate}>
            <Download size={14} style={{ marginRight: "6px" }} /> Download CSV Template
          </Button>
          <Button
            variant={dryRun ? "primary" : "outline"}
            size="sm"
            onClick={() => setDryRun((v) => !v)}
          >
            Dry Run Validation: {dryRun ? "ENABLED (Simulation)" : "DISABLED (Live Import)"}
          </Button>
        </div>

        {canPickSociety ? (
          <div style={{ marginBottom: "20px" }}>
            <FormField label="Target Society / Property" required>
              <SelectDropdown
                value={societyId}
                onChange={(e) => setSocietyId(e.target.value)}
                options={societies.map((s) => ({
                  label: `${s.society_name} (${s.society_type || "Residential"})`,
                  value: String(s.society_id),
                }))}
              />
            </FormField>
          </div>
        ) : null}

        <div style={{ marginBottom: "20px" }}>
          <FormField label="Spreadsheet File" required helperText="Supported formats: .CSV, .XLSX, .XLS up to 10MB">
            <FileUpload
              accept=".csv,.xlsx,.xls"
              maxSizeMB={10}
              helperText="Click or drag CSV / Excel file here"
              onChange={(selectedFile) => setFile(selectedFile)}
            />
          </FormField>
        </div>

        <div className={styles.footer} style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "24px" }}>
          <Button
            onClick={handleImport}
            disabled={busy || !file}
            loading={busy}
          >
            {busy ? "Processing..." : dryRun ? "Run Validation Check" : "Import Residents Now"}
          </Button>
        </div>

        {result ? (
          <div className={styles.result} style={{ marginTop: "24px", padding: "16px", borderRadius: "12px", border: "1px solid var(--color-border)", background: "var(--color-surface-2)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "12px" }}>
              <CheckCircle size={18} color="var(--color-success)" />
              <h3 style={{ margin: 0, fontSize: "15px", fontWeight: 700 }}>Execution Summary</h3>
            </div>
            <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", marginBottom: "14px" }}>
              <StatusBadge status="ACTIVE" label={`Total Rows: ${result.summary?.total_rows ?? 0}`} />
              <StatusBadge status="APPROVED" label={`Created: ${result.summary?.created ?? 0}`} />
              <StatusBadge status="HELD_AT_GATE" label={`Skipped Existing: ${result.summary?.skipped_existing ?? 0}`} />
              {result.summary?.errors > 0 ? (
                <StatusBadge status="REJECTED" label={`Errors: ${result.summary?.errors ?? 0}`} />
              ) : null}
            </div>

            {Array.isArray(result.errors) && result.errors.length ? (
              <div style={{ marginTop: "12px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "13px", fontWeight: 600, color: "var(--color-danger)", marginBottom: "8px" }}>
                  <AlertTriangle size={15} />
                  <span>Row Errors Encountered</span>
                </div>
                <div style={{ maxHeight: "200px", overflowY: "auto", border: "1px solid var(--color-border)", borderRadius: "8px", padding: "10px", background: "var(--color-surface)" }}>
                  {result.errors.map((e, idx) => (
                    <div key={idx} style={{ fontSize: "12px", color: "var(--color-danger)", padding: "4px 0", borderBottom: "1px solid var(--color-border)" }}>
                      <strong>Row {e.row}:</strong> {e.reason}
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </AdminLayout>
  );
};

export default ResidentImport;
